import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import { Board, TitleArea, ContentArea as BaseContextArea, FormRow } from "components/LayoutParts";
import TextButton from "components/TextButton";
import font from "constants/font";
import AimoParkingEdgeDeviceStatusSwitcher from "pages/AimoParking/component/AimoParkingEdgeDeviceStatusSwitcher";
import { PARK_FLOW_URL } from "pages/AimoParking/constants/aimoParkingSiteUrl";
import { useFindEdgeDevice, useFindLogEdgeDevice } from "pages/AimoParking/EdgeDevice/api";
import { AIMO_PARKING_EDGE_DEVICE_STATUS } from "pages/AimoParking/utils/aimoParkingEdgeDeviceStatusUtil";
import { dateFormat } from "utils/dateTimeUtils";

const Label = styled.div`
  min-width: 160px;
  margin: 0 5px;
`;

const Value = styled.div`
  font-size: ${font.size16};
`;

const FormWrap = styled.div`
  flex: 1;
`;

const ContentArea = styled(BaseContextArea)`
  flex-direction: row;
  margin-bottom: 0;
`;

const ImageWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Image = styled.img`
  width: 80px;
  height: 80px;
`;

const PropertyTitle = styled.div`
  font-size: ${font.size24};
`;

const Margin = styled.div<{ top: number }>`
  margin-top: ${({ top }) => top}px;
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 16px 12px 0 -12px;
  right: -32px;
`;

const EdgeDeviceDetail = () => {
  const navigate = useNavigate();
  const { id: edgeDeviceId } = useParams();
  const { data: edgeDevice } = useFindEdgeDevice({ edgeDeviceId: edgeDeviceId as string });
  const getEdgeDeviceLog = useFindLogEdgeDevice();

  return (
    <Board>
      <TitleArea>
        <Breadcrumb
          currentPageName={`${edgeDevice?.serialNumber ?? "読み込み中"}`}
          breadcrumbItems={[
            {
              pageName: `エッジデバイス`,
              onClick: () => navigate(`/aimo_parking/edge_device`),
            },
          ]}
        />
      </TitleArea>
      {edgeDevice && (
        <>
          <ContentArea>
            <FormWrap>
              <FormRow>
                <Label>ID</Label>
                <Value>{edgeDevice.id}</Value>
              </FormRow>
              <FormRow>
                <Label>シリアルナンバー</Label>
                <Value>{edgeDevice.serialNumber}</Value>
              </FormRow>
              <FormRow>
                <Label>NVR</Label>
                <Value>{edgeDevice.nvr}</Value>
              </FormRow>
              <FormRow>
                <Label>ステータス</Label>
                <Value>
                  <AimoParkingEdgeDeviceStatusSwitcher status={edgeDevice.status} />
                </Value>
              </FormRow>
              <FormRow>
                <Label>最終接続日時</Label>
                <Value>
                  {edgeDevice.lastConnectionTime ? dateFormat(edgeDevice.lastConnectionTime, "YYYY/MM/DD HH:mm") : ""}
                </Value>
              </FormRow>
              <FormRow>
                <Label>AIバージョン</Label>
                <Value>
                  {edgeDevice.currentAiSoftware?.version ?? "-"}
                  {edgeDevice.currentAiSoftware?.releaseDate != null && (
                    <>（{dateFormat(edgeDevice.currentAiSoftware.releaseDate, "YYYY/MM/DD")}）</>
                  )}
                </Value>
              </FormRow>
              <FormRow>
                <Label>画像</Label>
                <Value>
                  <ImageWrap>
                    {edgeDevice.images &&
                      Object.values(edgeDevice.images).map((image, i) => {
                        return <Image src={image} alt={`device_image_${i}`} key={`image_${i}`} />;
                      })}
                  </ImageWrap>
                </Value>
              </FormRow>
            </FormWrap>
            <ButtonWrap>
              <Button
                label="エッジデバイスを更新"
                onClick={() => navigate(`/aimo_parking/edge_device/${edgeDevice.id}/edit`)}
                type="primary"
                width="180px"
              />
              <Margin top={16} />
              <Button
                label="AIバージョンを変更"
                onClick={() => navigate(`/aimo_parking/edge_device/${edgeDevice.id}/ai_version`)}
                type="primary"
                width="180px"
              />
              {edgeDevice.status !== AIMO_PARKING_EDGE_DEVICE_STATUS.REMOVED && (
                <>
                  <Margin top={16} />
                  <Button
                    label="ステータスを変更"
                    onClick={() => navigate(`/aimo_parking/edge_device/${edgeDevice.id}/status`)}
                    type="primary"
                    width="180px"
                  />
                </>
              )}
              <Margin top={16} />
              <Button
                label="ログを取得"
                onClick={async () => {
                  const result = await getEdgeDeviceLog(edgeDevice?.serialNumber);

                  const newWindow = window.open("", "_blank", "width=600,height=400");
                  if (newWindow && result.status == 200) {
                    const formattedLog = result.data.log.replace(/\n/g, "<br>");
                    newWindow.document.write(`<div>${formattedLog}</div>`);
                  }
                }}
                type="primary"
                width="180px"
              />
            </ButtonWrap>
          </ContentArea>
          {edgeDevice.property && (
            <>
              <Margin top={32} />
              <PropertyTitle>駐車場情報</PropertyTitle>
              <Margin top={16} />
              <ContentArea>
                <FormWrap>
                  <FormRow>
                    <Label>ID</Label>
                    <Value>{edgeDevice.property.id}</Value>
                  </FormRow>
                  <FormRow>
                    <Label>駐車場名</Label>
                    <TextButton
                      label={edgeDevice.property.name}
                      onClick={() => {
                        if (edgeDevice?.property == null) return;

                        window.open(
                          `${PARK_FLOW_URL}/search/${edgeDevice.property.id}`,
                          "_blank",
                          "width=800,height=600"
                        );
                      }}
                    />
                  </FormRow>
                </FormWrap>
              </ContentArea>
            </>
          )}
        </>
      )}
    </Board>
  );
};

export default EdgeDeviceDetail;
